import { PurchaseOrder, POInsights } from '../../lib/api';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Package, ExternalLink, AlertCircle, Truck } from 'lucide-react';
import { getLineStatusBadge } from './PODetailHelpers';
import { usePODetailFormatters } from './usePODetailFormatters';
import { useFormatters } from '../../contexts/PreferencesContext';
import { nextActionFor } from './nextAction';
import PODetailLineLiveRow, { lineTabHref } from './PODetailLineLiveRow';
import PODeliveryPerformanceChart from './PODeliveryPerformanceChart';
import { CUSTOMER_REQUIRED_DATE, MRP_SCHEDULED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE_SHORT } from '../../config/dateLabels';

interface PODetailLinesTabProps {
  po: PurchaseOrder;
  /** Drives the supplier-only columns; derived in the parent from the authenticated user's platform. */
  isSupplierUser: boolean;
  /** Customer review 11 Sep (item 26): per-line NCR / ASN and the order's delivery performance. */
  insights: POInsights | null;
  insightsState: 'loading' | 'ready' | 'error';
}

/**
 * The Lines panel of the Purchase Order Details modal.
 *
 * Customer review 11 Sep (item 26) — "if we are giving an overview of the
 * lines we need to be giving the full overview": MRP/ERP and ODD dates, the
 * Next Action in place of the goods-receipt status ("pending"), Contract Price
 * and PPV, the live change request / ASN / NCR, and a delivery-performance
 * donut for the order — each clicking through to its detail.
 */
export function PODetailLinesTab({ po, isSupplierUser, insights, insightsState }: PODetailLinesTabProps) {
  const { formatCurrency, formatDate, preferences } = usePODetailFormatters();
  // CR-7262: quantities keep their decimals. These renderings moved here
  // from PODetailModal in the CR-7258 split, and the rounding moved with them.
  const { formatQuantity } = useFormatters();

  if (!po.lines || po.lines.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No lines available for this purchase order</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {insights && (
        <PODeliveryPerformanceChart poNumber={po.po_number} performance={insights.delivery_performance} />
      )}
      {po.lines.map((line) => {
        const nextAction = nextActionFor(line);
        // The line's ODD as the server derives it (odd_date) — the same value
        // the line Detail tab's ODD field shows.
        const odd = line.odd_date ?? line.customer_required_date;
        return (
          <Card key={line.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                      {line.line_number}
                    </span>
                    <a
                      href={`/purchasing?tab=po_lines&line=${line.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group cursor-pointer hover:bg-blue-50 rounded-lg px-2 py-1 -ml-2 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-mono font-semibold group-hover:text-blue-700 transition-colors">{line.part_number}</div>
                          <div className="text-sm text-muted-foreground">{line.part_description?.trim() || '-'}</div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {line.on_hold && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      <AlertCircle className="h-3 w-3" />
                      On Hold
                    </div>
                  )}
                  {/* review item 26b: the PO Lines grid's Next Action, not the
                      goods-receipt status the old "pending" badge showed. */}
                  {nextAction.text !== '-' && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${nextAction.className}`} title="Next Action">
                      {nextAction.text}
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={`grid grid-cols-2 ${isSupplierUser ? 'md:grid-cols-3' : 'md:grid-cols-5'} gap-4`}>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Quantity</label>
                  <div className="font-medium mt-1">
                    {formatQuantity(line.quantity)} {line.unit_of_measure}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Unit Price</label>
                  <div className="font-medium mt-1">{formatCurrency(line.unit_price, po.currency, preferences.unit_price_decimals)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Total Price</label>
                  <div className="font-medium mt-1">{formatCurrency(line.total_price, po.currency, 2)}</div>
                </div>
                {/* review item 26d. Customer-only: the serializer withholds
                    contract pricing and PPV from suppliers (CR-6189). */}
                {!isSupplierUser && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Contract Price</label>
                      <div className="font-medium mt-1">
                        {line.contract_price ? (
                          <a href={lineTabHref(line.id, 'pricebooks')} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {formatCurrency(line.contract_price, po.currency, 2)}
                          </a>
                        ) : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">PPV</label>
                      <div className="font-medium mt-1">{line.ppv ?? '-'}</div>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
                {/* DATE-FIELD-CANON PR 2 (15 Sep 2026): the Customer
                    Required Date first — the date expediting moves; this card
                    never had it. review item 26a: MRP/ERP and ODD at a glance. */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">{CUSTOMER_REQUIRED_DATE}</label>
                  <div className="font-medium mt-1">{formatDate(line.customer_required_date || '')}</div>
                </div>
                <div title={line.mrp_scheduled_date ? undefined : "Not supplied by this customer's ERP import"}>
                  <label className="text-xs font-medium text-muted-foreground">{MRP_SCHEDULED_DATE}</label>
                  <div className="font-medium mt-1">{formatDate(line.mrp_scheduled_date || '')}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">{ORIGINAL_REQUIRED_DATE}</label>
                  <div className="font-medium mt-1">{formatDate(odd || '')}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">{SUPPLIER_PROMISE_DATE_SHORT}</label>
                  <div className="font-medium mt-1">
                    {formatDate(line.supplier_promise_date || '')}
                    {line.original_required_date && line.supplier_promise_date &&
                     line.supplier_promise_date !== line.original_required_date && (
                      <div className="text-xs text-orange-600">
                        (was {formatDate(line.original_required_date)})
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">{getLineStatusBadge(line)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Received</label>
                  <div className="font-medium mt-1 text-green-700">{formatQuantity(line.received_qty)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Outstanding</label>
                  <div className="font-medium mt-1 text-orange-700">{formatQuantity(line.outstanding_qty)}</div>
                </div>
                {/* CR-4716: Buyer ID hidden from supplier users */}
                {!isSupplierUser && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Buyer ID</label>
                    <div className="font-medium mt-1">{line.buyer_id || '-'}</div>
                  </div>
                )}
              </div>

              <PODetailLineLiveRow
                line={line}
                insight={insights?.lines[String(line.id)]}
                insightsState={insightsState}
                formatDate={formatDate}
                formatQuantity={formatQuantity}
              />

              {/* GRNs Section */}
              {line.grns && line.grns.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    <a href={lineTabHref(line.id, 'grn')} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline">
                      Goods Receipt Notes ({line.grns.length})
                    </a>
                  </div>
                  <div className="space-y-2">
                    {line.grns.map((grn) => {
                      // review item 30c, carried here: rejected includes the
                      // NCRs raised against the receipt (GRN.quantity_rejected
                      // is never populated). Inspection is not shown (msg 30e).
                      const rejected = parseFloat(grn.quantity_rejected || '0')
                        + parseFloat(insights?.grn_ncr_rejected[String(grn.id)] || '0');
                      return (
                        <div key={grn.id} className="bg-muted/50 rounded-lg p-3 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-medium text-primary">{grn.grn_number}</span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <div>
                              <span className="font-medium">Received:</span>{' '}
                              {formatDate(grn.receipt_date)}
                            </div>
                            <div>
                              <span className="font-medium">Qty:</span>{' '}
                              <span className="text-foreground">{formatQuantity(grn.quantity_received)}</span>
                              {rejected > 0 && (
                                <span className="text-red-600 ml-1">(-{formatQuantity(rejected)} rejected)</span>
                              )}
                            </div>
                            {grn.batch_number && (
                              <div>
                                <span className="font-medium">Batch:</span>{' '}
                                <span className="text-foreground">{grn.batch_number}</span>
                              </div>
                            )}
                            {grn.packing_slip_number && (
                              <div>
                                <span className="font-medium">Packing Slip:</span>{' '}
                                <span className="text-foreground">{grn.packing_slip_number}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
